<template>
  <div class="btb">
    <FormPlanNav
      :sections="planSections"
      :active="activeKey"
      :done="doneCount"
      :total="confirmableCount"
      :progress-label="progressLabel"
      nav-label="Plan de l’analyse du besoin"
      testid-prefix="besoin-plan"
      class="btb-plan"
      @update:active="goTo"
    />

    <section class="btb-panel">
      <div ref="bodyRef" class="btb-body">
        <LoadingSpinner v-if="loading" position="inline" />

        <template v-else-if="quote">
          <!-- M9 : l'échec du serveur s'affiche ICI, sur le formulaire encore
               rempli. Un toast s'efface alors que le tunnel est encore ouvert
               et le problème encore là. -->
          <DialogNotice v-if="error" :tone="preview ? 'info' : 'danger'" :title="preview ? 'Mode aperçu' : 'L’enregistrement a échoué'">
            {{ error }}
          </DialogNotice>

          <!-- Aperçu : dit une fois, sur la première section. Le repère
               permanent est le badge de l'en-tête. -->
          <DialogNotice
            v-if="preview && activeKey === 'personal'"
            tone="warning"
            icon="fas fa-flask"
            title="Ceci est un aperçu"
          >
            Les données affichées sont des exemples et rien n’est enregistré. Tous les
            questionnaires de qualification actifs y sont montrés — sur une vraie demande,
            seuls ceux liés à la formation choisie apparaissent.
          </DialogNotice>

          <DialogNotice v-if="isQuoteSigned" tone="info" title="Ce devis est signé">
            Les informations sont en lecture seule.
          </DialogNotice>

          <!-- Un décochage automatique se DIT : sans un mot, une case qui
               saute toute seule passe pour un bug. -->
          <DialogNotice v-if="autoUncheckNotice" tone="warning" title="Une confirmation ne vaut plus">
            {{ autoUncheckNotice }}
          </DialogNotice>

          <!-- Le titre ENTIER de la section : c'est lui qui porte le nom que
               le plan abrège quand il dépasse deux lignes. -->
          <header class="btb-head">
            <span class="btb-head-icon" aria-hidden="true"><i :class="activeSection.icon"></i></span>
            <h3 class="btb-head-title">{{ activeSection.title }}</h3>
            <!-- CONFIRMÉE, ET NON VERROUILLÉE : sur un devis signé, `sectionLocked`
                 vaut vrai partout sans que personne n'ait rien confirmé — le badge
                 contredisait alors le plan, qui comptait « 0 sur 9 ». -->
            <Badge
              v-if="activeSection.confirmable && sectionsConfirmed[activeKey] === true"
              variant="success"
              size="xs"
            >
              Confirmée
            </Badge>
          </header>

          <p v-if="activeSection.hint" class="btb-head-hint">{{ activeSection.hint }}</p>

          <!-- ===================== LES SECTIONS ===================== -->
          <template v-if="activeKey === 'personal'">
            <QuoteDetailPersonalInfo
              bare
              :quote="shownQuote"
              :is-editing="sectionEditing('personal')"
              :edited-quote="editedQuote"
            >
              <template #besoin-email>
                <BesoinEmailConfirm
                  :model-value="isEmailConfirmed"
                  :email-valid="canConfirmEmail"
                  :disabled="sectionLocked('personal')"
                  :error="sectionErrors('personal').email_confirme"
                  @update:model-value="setEmailConfirmed"
                />
              </template>
            </QuoteDetailPersonalInfo>

            <!-- Un particulier n'a que son adresse en plus : elle se demande
                 ici, avec son historique (retour Figma du 2026-10-05), plutôt
                 que dans une section à elle. Le composant ne rend rien pour
                 une entreprise. -->
            <QuoteDetailParticulier
              bare
              :quote="shownQuote"
              :is-editing="sectionEditing('personal')"
              :edited-quote="editedQuote"
              :get-value="getValue"
            />
            <QuoteDetailClientHistory
              v-if="!preview && quote && getValue('client_type') === 'particulier'"
              :email="quote.email"
              :siret="ficheSiretOf(quote)"
            />
          </template>

          <template v-else-if="activeKey === 'client'">
            <QuoteDetailProfessionnel
              bare
              :quote="shownQuote"
              :is-editing="sectionEditing('client')"
              :edited-quote="editedQuote"
              :get-value="getValue"
              @refresh="handleInseeRefresh"
            />

            <!-- Ce que le commercial a besoin de savoir avant d'argumenter :
                 ce que le client a déjà demandé, et avec quelles structures
                 voisines Alertis travaille déjà. En lecture, rien à confirmer. -->
            <template v-if="!preview && quote">
              <QuoteDetailClientHistory :email="quote.email" :siret="ficheSiretOf(quote)" />
              <QuoteDetailLinkedCompanies :quote-id="quote.id" />
            </template>
          </template>

          <QuoteDetailFormation
            v-else-if="activeKey === 'formation'"
            bare
            :quote="shownQuote"
            :is-editing="sectionEditing('formation')"
            :edited-quote="editedQuote"
            :loading-formations="loadingFormations"
            :categories="categories"
            :available-formations="availableFormations"
            :selected-formation="selectedFormation"
            :formation-lieu-options="formationLieuOptions"
            :get-value="getValue"
            @category-change="onCategoryChange"
            @formation-change="onFormationChange"
            @lieu-change="onLieuChange"
          >
            <template #besoin-extra>
              <div class="btb-formation-extra">
                <Divider />
                <!-- La session INTER se choisit ICI, sous le lieu qui la fait
                     apparaître (retour Figma du 2026-10-05) : elle avait sa
                     propre section juste après, pour trois champs. -->
                <QuoteDetailInter
                  bare
                  :quote="shownQuote"
                  :is-editing="sectionEditing('formation')"
                  :edited-quote="editedQuote"
                  :available-villes="availableVilles"
                  :available-sessions="availableSessions"
                  :selected-session="selectedSession"
                  :get-value="getValue"
                  @ville-change="onVilleChange"
                />
                <!--
                  Ni « Nombre de participants », ni « Nombre de groupes », ni
                  les besoins spécifiques ici : le bloc Formation juste au-dessus
                  porte les trois, sur les propriétés que le devis enregistre.
                  Deux saisies pour une seule donnée ne se distinguaient que par
                  leur libellé, et pouvaient se contredire — le commentaire, lui,
                  était même vide alors que le client l'avait déjà écrit.
                -->
                <DetailItem v-if="tarifEstime" label="Tarif estimé" :copyable="false">
                  <strong>{{ tarifEstime }}</strong>
                  <small v-if="detailTarifEstime" class="btb-tarif-detail">{{ detailTarifEstime }}</small>
                </DetailItem>
                <!-- Les questions de qualification de la formation se posent
                     ICI, dans la foulée du nombre de participants (retour
                     Figma du 2026-09-11) : elles avaient leur propre section
                     plus bas, que le commercial atteignait après l'argumentaire. -->
                <BesoinQuestionnairesPanel
                  v-if="hasSpecificQuestionnaires"
                  :questionnaires="specificQuestionnaires"
                  :disabled="sectionLocked('formation')"
                  :errors="sectionErrors('formation')"
                  :preview="preview"
                  :get-answer="getAnswer"
                  :questions-posees="questionsPosees"
                  :is-answer-from-client="isAnswerFromClient"
                  @update-answer="handleUpdateAnswer"
                  @update-commentaire="handleUpdateComment"
                />
              </div>
            </template>
          </QuoteDetailFormation>

          <BesoinCommunSection
            v-else-if="activeSection.commun"
            bare
            :section-key="activeKey"
            :model-value="besoinQuestionnaire"
            :disabled="sectionLocked(activeKey)"
            :errors="sectionErrors(activeKey)"
          />

          <QuoteDetailArgumentaire
            v-else-if="activeKey === 'argumentaire'"
            bare
            :formation-nom="getValue('formation_nom')"
          />

          <QuoteDetailAvailability
            v-else-if="activeKey === 'disponibilites'"
            bare
            :quote="shownQuote"
            :is-editing="sectionEditing('disponibilites')"
            :edited-quote="editedQuote"
            allow-periods
            @update:dates-disponibilites="handleDatesDisponibilitesChange"
            @update:dates-periodes="handleDatesPeriodesChange"
            @update:dates-inconnues="handleDatesInconnuesChange"
          />

          <QuoteDetailIntraLocation
            v-else-if="activeKey === 'lieu'"
            bare
            :quote="shownQuote"
            :is-editing="sectionEditing('lieu')"
            :edited-quote="editedQuote"
            :get-value="getValue"
          />

          <QuoteDetailFinancement
            v-else-if="activeKey === 'financement'"
            bare
            :quote="shownQuote"
            :is-editing="sectionEditing('financement')"
            :edited-quote="editedQuote"
          />

          <BesoinVigilancePanel
            v-else-if="activeKey === 'vigilance'"
            :unconfirmed-sections="unconfirmedSections"
            :missing-fields="missingFields"
            :comment="vigilanceComment"
            :disabled="isQuoteSigned"
            :signed="isQuoteSigned"
            @go="goToLabel"
            @update:comment="vigilanceComment = $event"
          >
            <template #devis>
              <BesoinElementsDevis :quote="shownQuote" />
              <BesoinCarteFormateurs
                v-if="!preview"
                :quote="shownQuote"
                :selected-formation="selectedFormation"
              />
            </template>
          </BesoinVigilancePanel>
        </template>
      </div>

      <!-- Rien à confirmer et rien après (le Point de vigilance) : pas de barre.
           Elle n'y disait plus que « Ce bilan se remplit tout seul », la même
           idée que la phrase d'explication retirée (retour Figma). -->
      <BesoinConfirmBar
        v-if="quote && !loading && (activeConfirmable || nextSection)"
        :confirmed="sectionsConfirmed[activeKey] === true"
        :section-key="activeKey"
        :confirmable="activeConfirmable"
        :refused="refusedCount(activeKey)"
        :next-label="nextSection ? nextSection.planLabel : null"
        @confirm="confirmAndNext"
        @reopen="reopenSection(activeKey)"
        @next="goTo(nextSection.key)"
      />
    </section>
  </div>
</template>

<script setup>
/**
 * LE CORPS DU TUNNEL « ANALYSE DU BESOIN » — plan à gauche, une section à la
 * fois à droite, le bouton « Confirmer et continuer » à demeure sous le panneau.
 *
 * POURQUOI IL EST ICI ET NON DANS LA FENÊTRE. Deux écrans le rendent : la
 * fenêtre `QuoteBesoinDialog` (ouverte depuis le bandeau d'appel) et la page
 * `views/QuoteBesoinTunnel.vue`, qui sert la route pleine page
 * /quotes/:id/analyse-besoin et son aperçu. Le même questionnaire, le même
 * plan, les mêmes douze sections : la deuxième occurrence l'a fait entrer ici
 * plutôt que d'en produire une copie qui divergerait au premier correctif.
 *
 * CE QU'IL NE FAIT PAS : le cadre. Ni pied, ni titre de page, ni garde de
 * fermeture — la fenêtre les prend de `Dialog`, la page les rend elle-même.
 * Il ne connaît que le plan et les sections.
 *
 * PRÉSENTATIONNEL (M10) : ni appel réseau ni toast. Tout l'état vient de
 * `useQuoteBesoinForm`, monté par l'écran qui le porte.
 */
import { computed, nextTick, ref, watch } from 'vue'
import { DialogNotice } from '../ui/dialog'
import { Badge, Divider, LoadingSpinner } from '../ui'
import { DetailItem } from '../data'
import {
  QuoteDetailPersonalInfo,
  QuoteDetailParticulier,
  QuoteDetailProfessionnel,
  QuoteDetailFormation,
  QuoteDetailArgumentaire,
  QuoteDetailAvailability,
  QuoteDetailIntraLocation,
  QuoteDetailInter,
  QuoteDetailFinancement,
  QuoteDetailClientHistory,
  QuoteDetailLinkedCompanies
} from '../quote-detail'
import BesoinCommunSection from '../validation/BesoinCommunSection.vue'
import BesoinEmailConfirm from '../validation/BesoinEmailConfirm.vue'
import { FormPlanNav } from '../ui/form'
import BesoinConfirmBar from './BesoinConfirmBar.vue'
import BesoinQuestionnairesPanel from './BesoinQuestionnairesPanel.vue'
import BesoinVigilancePanel from './BesoinVigilancePanel.vue'
import BesoinElementsDevis from './BesoinElementsDevis.vue'
import BesoinCarteFormateurs from './BesoinCarteFormateurs.vue'
import { BESOIN_COMMUN_SECTIONS } from '../../composables/questionnaires/besoin-commun-schema.js'
import { focusFirstInvalid } from '../../utils/focusFirstInvalid.js'
import { useShownQuote } from '../../composables/quotes/useShownQuote.js'
import { ficheSiretOf } from '../../utils/contactRoute.js'
import { accesEquipe, fournirDocumentsReponses } from '../../composables/questionnaires/useReponseDocuments.js'

const props = defineProps({
  /** Tout l'état du tunnel, tel que `useQuoteBesoinForm` le rend. */
  form: { type: Object, required: true },
  /** Aperçu : données d'exemple, rien d'enregistré. */
  preview: { type: Boolean, default: false },
  /** Section à afficher au montage (et à chaque réouverture de la fenêtre). */
  initialSection: { type: String, default: 'personal' }
})

const emit = defineEmits(['update:section'])

// Les documents joints aux réponses, déposés par le commercial sur CETTE demande.
// Pas en aperçu : il n'y a pas de demande où les ranger.
if (!props.preview) {
  const idDeLaDemande = computed(() => props.form.quote?.value?.id ?? null)
  fournirDocumentsReponses(accesEquipe(idDeLaDemande), idDeLaDemande)
}

// L'état du composable, déballé pour le template. Les refs restent réactives.
const {
  quote, loading, isEditing, editedQuote, isQuoteSigned, getValue,
  loadingFormations, categories, availableFormations, selectedFormation, formationLieuOptions,
  availableVilles, availableSessions, selectedSession,
  onCategoryChange, onFormationChange, onLieuChange, onVilleChange,
  handleDatesDisponibilitesChange, handleDatesPeriodesChange, handleDatesInconnuesChange, handleInseeRefresh,
  tarifEstime, detailTarifEstime,
  besoinQuestionnaire, vigilanceComment, isEmailConfirmed, canConfirmEmail, setEmailConfirmed,
  specificQuestionnaires, hasSpecificQuestionnaires,
  getAnswer, handleUpdateAnswer, handleUpdateComment, sortedQuestions, questionsPosees,
  isAnswerFromClient,
  sectionsConfirmed, sectionLocked, sectionEditing, unconfirmedSections,
  confirmSection, reopenSection, sectionErrors, refusedCount,
  autoUncheckNotice,
  missingFields, missingBySection, error
} = props.form

// Ce qui sera enregistré, et non ce qui a été chargé (cf. useShownQuote).
const shownQuote = useShownQuote(quote, editedQuote, isEditing)

const activeKey = ref(props.initialSection)
const bodyRef = ref(null)

/**
 * LE PLAN, dans l'ordre de l'appel. `planLabel` est ce que porte la colonne,
 * `title` le nom entier porté par le panneau : les deux ne divergent que là où
 * le titre dépasserait deux lignes de plan.
 *
 * `when` décide de la présence : le bloc client n'existe que pour une
 * entreprise (l'adresse d'un particulier se demande avec ses informations
 * personnelles), le lieu qu'en INTRA (la session INTER se choisit dans
 * « Formation ») — exactement les mêmes conditions que `useBesoinSectionsConfirm`, pour que le plan ne propose
 * jamais une section qui ne compte pas dans « 4 sur 10 ».
 */
const ALL_SECTIONS = computed(() => {
  const clientType = quote.value ? getValue('client_type') : null
  const lieu = quote.value ? getValue('formation_lieu') : null
  const commun = BESOIN_COMMUN_SECTIONS.map((s) => ({
    key: s.key,
    planLabel: s.planLabel || s.title,
    title: s.title,
    icon: s.icon || 'fas fa-list-check',
    confirmable: true,
    commun: true
  }))

  return [
    {
      key: 'personal',
      planLabel: 'Informations personnelles',
      title: 'Informations personnelles',
      icon: 'fas fa-user',
      confirmable: true
    },
    {
      key: 'client',
      planLabel: 'Informations professionnelles',
      title: 'Informations professionnelles',
      icon: 'fas fa-building',
      confirmable: true,
      when: () => clientType === 'professionnel'
    },
    {
      key: 'formation',
      planLabel: 'Formation',
      title: 'Formation',
      icon: 'fas fa-graduation-cap',
      confirmable: true
    },
    // Le lieu se demande dans la foulée de la formation : c'est la même
    // question posée au client, et l'appel suit cet enchaînement.
    {
      key: 'lieu',
      planLabel: 'Détails du lieu de formation',
      title: 'Détails du lieu de formation',
      icon: 'fas fa-location-dot',
      confirmable: true,
      when: () => lieu === 'INTRA' || lieu === 'LOCATION'
    },
    ...commun,
    {
      key: 'argumentaire',
      planLabel: 'Argumentaire commercial',
      title: 'Argumentaire commercial',
      icon: 'fas fa-comments',
      confirmable: false,
      hint: 'À lire au client. Rien à remplir dans cette section.'
    },
    {
      key: 'disponibilites',
      planLabel: 'Disponibilités du client',
      title: 'Disponibilités du client',
      icon: 'fas fa-calendar-days',
      confirmable: true
    },
    {
      key: 'financement',
      planLabel: 'Financement',
      title: 'Financement',
      icon: 'fas fa-euro-sign',
      confirmable: true
    },
    {
      key: 'vigilance',
      planLabel: 'Point de vigilance',
      title: 'Point de vigilance',
      icon: 'fas fa-triangle-exclamation',
      confirmable: false
    }
  ].filter((s) => (s.when ? s.when() : true))
})

const activeSection = computed(
  () => ALL_SECTIONS.value.find((s) => s.key === activeKey.value) || ALL_SECTIONS.value[0]
)

/** Les sections qui comptent dans « n sur N » : celles qui se confirment. */
const confirmableSections = computed(() =>
  ALL_SECTIONS.value.filter((s) => s.confirmable && (s.countsWhen ? s.countsWhen() : true))
)
const confirmableCount = computed(() => confirmableSections.value.length)
const doneCount = computed(
  () => confirmableSections.value.filter((s) => sectionsConfirmed[s.key] === true).length
)

/** La phrase de progression appartient à l'écran, pas au plan (cf. FormPlanNav). */
const progressLabel = computed(() =>
  doneCount.value <= 1
    ? `${doneCount.value} section confirmée sur ${confirmableCount.value}`
    : `${doneCount.value} sections confirmées sur ${confirmableCount.value}`
)

const planSections = computed(() =>
  ALL_SECTIONS.value.map((s) => {
    const counts = s.confirmable && (s.countsWhen ? s.countsWhen() : true)
    const done = counts && sectionsConfirmed[s.key] === true
    const missing = done ? 0 : (missingBySection.value[s.key] || 0)
    return {
      key: s.key,
      label: s.planLabel,
      done,
      readOnly: !s.confirmable,
      missing,
      stateText: !s.confirmable
        ? 'section de lecture'
        : (done ? 'section confirmée' : 'section à confirmer')
    }
  })
)

const nextSection = computed(() => {
  const i = ALL_SECTIONS.value.findIndex((s) => s.key === activeKey.value)
  return i >= 0 ? ALL_SECTIONS.value[i + 1] || null : null
})

/**
 * Y a-t-il quelque chose à confirmer ici ? Pas sur un bloc de lecture, ni sur
 * une section qui ne compte pas, ni sur un devis signé : la barre n'y propose
 * alors que « Continuer ».
 */
const activeConfirmable = computed(() => {
  const s = activeSection.value
  return s.confirmable && (s.countsWhen ? s.countsWhen() : true) && !isQuoteSigned.value
})

/**
 * Le geste unique de la barre : confirmer, puis ouvrir la section suivante.
 * Refusée — une réponse obligatoire manque —, la section reste ouverte, ses
 * champs manquants passent en rouge et le premier vient sous les yeux.
 */
async function confirmAndNext() {
  const key = activeKey.value
  if (confirmSection(key)) {
    if (nextSection.value) goTo(nextSection.value.key)
    return
  }
  await nextTick()
  focusFirstInvalid(bodyRef.value, key)
}

/** Change de section et remet le panneau en haut : on arrive au début d'une
 *  section, jamais au milieu de la précédente. */
function goTo(key) {
  if (!key) return
  activeKey.value = key
  emit('update:section', key)
  if (bodyRef.value) bodyRef.value.scrollTop = 0
}

/**
 * Depuis le bilan : le libellé cliqué désigne une section du plan. Comparé
 * sans la casse : le bilan écrit « Informations Professionnelles », le plan
 * « Informations professionnelles », et le lien ne menait nulle part.
 */
function goToLabel(label) {
  const wanted = String(label).toLowerCase()
  const found = ALL_SECTIONS.value.find(
    (s) => s.planLabel.toLowerCase() === wanted || s.title.toLowerCase() === wanted
  )
  if (found) goTo(found.key)
}

/** Depuis la phrase du pied : droit à la première section qui bloque. */
function goToFirstBlocking() {
  const firstUnconfirmed = confirmableSections.value.find((s) => sectionsConfirmed[s.key] !== true)
  if (firstUnconfirmed) {
    goTo(firstUnconfirmed.key)
    return
  }
  const firstMissing = ALL_SECTIONS.value.find((s) => (missingBySection.value[s.key] || 0) > 0)
  if (firstMissing) goTo(firstMissing.key)
}

// La fenêtre sert successivement plusieurs demandes : quand elle redemande la
// première section, le corps y revient sans être remonté.
watch(() => props.initialSection, (key) => { if (key && key !== activeKey.value) goTo(key) })

defineExpose({ goTo, goToFirstBlocking, activeKey })
</script>

<style scoped>
/* Deux colonnes qui se distinguent par leur fond, sans filet entre elles :
   le contraste suffit, et il ne fabrique pas un trait de plus. */
.btb {
  height: 100%;
  min-height: 0;
  display: grid;
  grid-template-columns: 288px minmax(0, 1fr);
}

.btb-plan {
  min-height: 0;
}

.btb-panel {
  display: flex;
  flex-direction: column;
  min-height: 0;
  min-width: 0;
  background: var(--color-bg-primary);
}

/* Le panneau défile, le cadre non : le plan, la barre de confirmation et le
   pied restent en place à toute hauteur d'écran. */
.btb-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: var(--spacing-6);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-5);
}

.btb-head {
  display: flex;
  align-items: center;
  gap: var(--spacing-3);
}

/* Pastille d'icône de section : mêmes 28 px que `CollapsibleSection`, dont le
   mode « nu » retire justement l'en-tête que le tunnel reprend ici. */
.btb-head-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: var(--radius-lg);
  background: var(--color-primary-lightest);
  color: var(--color-primary);
  font-size: 0.875rem;
  line-height: 1;
  flex-shrink: 0;
}

.btb-head-icon i {
  line-height: 1;
  display: block;
}

.btb-head-title {
  margin: 0;
  padding: 0;
  min-width: 0;
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--color-primary-dark);
  line-height: 1.4;
  border: none;
}

.btb-head-hint {
  margin: calc(-1 * var(--spacing-3)) 0 0;
  font-size: var(--font-size-sm);
  line-height: 1.5;
  color: var(--color-text-secondary);
}

/* Champs de besoin injectés dans la carte Formation. */
.btb-formation-extra {
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
}

/* Le détail du calcul se lit sous le montant, sans lui disputer l'attention. */
.btb-tarif-detail {
  display: block;
  margin-top: var(--spacing-xs);
  color: var(--color-text-tertiary);
}

/* Sous 1024 px, le plan n'a plus la place d'occuper une colonne : il passe
   au-dessus du panneau, en bande défilante horizontalement. */
@media (max-width: 1024px) {
  .btb {
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: auto minmax(0, 1fr);
  }

  .btb-plan {
    border-bottom: 1px solid var(--color-border);
  }
}

@media (max-width: 768px) {
  .btb-body {
    padding: var(--spacing-md);
  }
}
</style>
